import md5 from './md5.js'
import aes from './aes.js'
import axios from 'axios'
import utils from './common-utlis.js'
import { LOCALE_STORAGE_KEY } from '../i18n'
import {
	config,
	env,
	currInterface,
	interlinkAuthInfo,
	jkyminiAuthInfo,
	toCAppKey,
	toCBusinessTypeCode,
	jmallServiceName
} from '../config.js'
import {
	methodList,
	isJkyminiMethod,
	isToCLoginMethod
} from './request-method-list.js'

// ===== 常量定义 =====

// 签名密钥（PC 专用）
const signKey = 'differcxztymylch'; //CECC95A6E3DDD6818E0E1713CF31466A

// C 端登录固定假账号（用于游客态假登录，参考小程序 app.js visitFakeLogin）
const VISITOR_FAKE_ACCOUNT = '18181818181818188';

// ====================== 商城版本 / 灰度切换 ======================

/**
 * 获取吉商城接口当前版本（用作 jkymini 链路的 origin_name / businessTypeCode 前置参数）
 *  - 走老链路（sys.getsetting 不以 jmall. 开头，不走 jkymini，不需要 token）
 *  - 必须把 jmall_api_version 写入 localStorage.pc_jmall_api_version，
 *    供 __completeDataForCAuth 计算 businessTypeCode 使用。
 *    不写则 jkymini 网关拿到的 businessTypeCode 永远是 'TOC_LOGIN_JMALL'，
 *    即便实际是 jmall-single 版本，token 与 origin_name 也会不匹配，
 *    表现为后续 jkymini 请求一直用"旧 token"。
 *  - 返回版本字符串：'jmall' / 'jmall-single'
 */
function getJmallApiVersion() {
    return new Promise((resolve, reject) => {
        // === 直接构造老链路请求体，绕开 jackyunPost 的 jkymini 判定 ===
        const data = {
            method: "sys.getsetting",
            content: "jmall_api_version,pcmall_version",
            // 老链路需要的公共参数（与 jackyunPost 老链路分支保持一致）
            target_member_name: memberName,
            sub_member_name: subMemberName,
            client_name: "pc",
            version_timestamp: versionTimestamp,
            timestamp: timestamp()
        };
        // 老链路需要 innerSign 签名
        data.innerSign = getInnerSign(data);

        // 直接打到 admin 后台（老链路），不走 jkymini 网关
        axios.post(config.jackyunApiUrl, data).then((response) => {
            const res = response && response.data;
            let apiVersion = "jmall";
            if (res && res.code == 200 && res.data) {
                if (res.data.jmall_api_version) {
                    apiVersion = res.data.jmall_api_version;
                }
                if (res.data.pcmall_version) {
                    localStorage.setItem("pcmall_version", res.data.pcmall_version);
                }
            }
            // 关键：写 pc_jmall_api_version（修复 App.vue 原方法没写这个 key 的 bug）
            localStorage.setItem("pc_jmall_api_version", apiVersion);
            resolve(apiVersion);
        }).catch((error) => {
            // 失败：兜底返回 'jmall'，并把异常向上抛
            console.warn('[getJmallApiVersion] 老链路请求失败：', error);
            localStorage.setItem("pc_jmall_api_version", "jmall");
            reject(error);
        });
    });
}

// ====================== 游客态 token 刷新阈值 ======================
const VISITOR_TOKEN_REFRESH_MS = 90 * 60 * 1000;

// 用户信息串
let memberEncryptStr = '';
// 吉客号
let memberName = '';
// 子商城账号
let subMemberName = '';

// 版本时间戳，用作前端强制刷新（当前版本：2022-11-24）  获取方式：new Date('2022-07-01').getTime()/1000
const versionTimestamp = 1691625600;

// ===== 吉客号相关 =====

/**
 * 获取吉客号
 */
function getUserName() {
	// 解析url传参
	if (window.location.search) {
		memberEncryptStr = window.location.search.substr(1);
		let strCode = window.decodeURIComponent(window.atob(memberEncryptStr));
		let userInfoStr = strCode ? strCode.substring(strCode.indexOf("=") + 1) : '';

		// 用户信息串：吉客号_子账号
		if (userInfoStr.includes("_") && !["jackyun_dev"].includes(userInfoStr)) {
			let userInfoArr = userInfoStr.split("_");
			memberName = userInfoArr[0];
			subMemberName = userInfoArr[1];
		} else {
			memberName = userInfoStr;
			subMemberName = "";
		}
	}

	// 本地默认情况处理
	if (!memberName && window.location.hostname == "localhost") {
		memberName = '420001'; //集成环境默认吉客号
		subMemberName = '';
	}


	// 比较吉客号与缓存中是否相同，不同则清除缓存
	if (memberName != localStorage.getItem("JackyunUserName") || (localStorage.getItem("SubMemberName") && subMemberName != localStorage.getItem("SubMemberName"))) {
		if (localStorage.getItem("JackyunUserName")) {
			const savedLocale = localStorage.getItem(LOCALE_STORAGE_KEY);
			localStorage.clear();
			savedLocale && localStorage.setItem(LOCALE_STORAGE_KEY, savedLocale);
		}
	}

	// 将吉客号信息记录到缓存
	localStorage.setItem("memberEncryptStr", memberEncryptStr);
	localStorage.setItem("JackyunUserName", memberName);
	localStorage.setItem("SubMemberName", subMemberName);

	return memberName;
}

/**
 * 获取时间戳（秒级，老 PC 的接口都用秒）
 */
function timestamp() {
	return parseInt(new Date().getTime() / 1000);
}

/**
 * 获取毫秒级时间戳（小程序的 jkymini 是毫秒级）
 */
function timestampMs() {
	return Date.now();
}

// ===== 签名相关 =====

/**
 * 获取验证签名
 * @param {Object} data
 * @param {String} [customKey] 自定义签名密钥（jkymini 用），不传则用默认 PC 密钥
 */
function sign(data, customKey) {
	let secret = customKey || signKey;
	// 将对象根据key进行升序排序
	let arrKey = new Array();
	for (let key in data) {
		arrKey.push(key);
	}
	arrKey.sort();

	// 获取MD5加密前的字符串，格式:secret+key+value+...+key+value+secret，key升序大写
	let signStr = '';
	for (let i = 0; i < arrKey.length; i++) {
		// 数据为空或者null则不进行签名计算
		if (data[arrKey[i]]) {
			// 大写key+value
			signStr += arrKey[i].toUpperCase() + data[arrKey[i]];
		}
	}
	signStr = secret + signStr + secret;

	// MD5加密并返回结果，结果大写
	return md5.md5(signStr).toUpperCase();
}

/**
 * 获取内部验证串（PC 后台 switch 自带的 innerSign）
 */
const getInnerSign = (data) => {
	// 将对象根据key进行升序排序
	let arrKey = new Array();
	for (let key in data) {
		arrKey.push(key);
	}
	arrKey.sort();

	// 获取MD5加密前的字符串，格式:signKey+key+value+...+key+value+signKey，key升序大写
	let signStr = '';
	for (let i = 0; i < arrKey.length; i++) {
		// 数据为空或者null则不进行签名计算（时间戳不参与计算，前端时间戳不准）
		if (arrKey[i] != "timestamp" && ![null, undefined, ""].includes(data[arrKey[i]])) {
			// 大写key+大写value（防止布尔值前后端大小写不一致情况）
			try {
				signStr += arrKey[i].toString().toUpperCase() + data[arrKey[i]].toString().toUpperCase();
			} catch (e) {
				console.log(data[arrKey[i]])
			}
		}
	}
	signStr = signKey + signStr + signKey;

	// MD5加密并返回结果，结果大写
	return md5.md5(signStr).toUpperCase();
}

// ===== 字符处理 =====

/**
 * 转换表情为字符串
 */
function utf16toEntities(str) {
	var patt = /[\ud800-\udbff][\udc00-\udfff]/g; // 检测utf16字符正则
	str = str.replace(patt, function(char) {
		var H, L, code;
		if (char.length === 2) {
			H = char.charCodeAt(0); // 取出高位
			L = char.charCodeAt(1); // 取出低位
			code = (H - 0xD800) * 0x400 + 0x10000 + L - 0xDC00; // 转换算法
			return "&#" + code + ";";
		} else {
			return char;
		}
	});
	return str;
}

/**
 * 转换字符串为表情
 */
function decodeHtmlEntity(str) {
	let nstr = str.replace(/&#(\d+);/g, function(match, dec) {
		return String.fromCharCode(dec);
	});
	return nstr;
}

/**
 * base64 编码（PC 浏览器版）
 */
function base64Encode(str) {
	return window.btoa(window.encodeURIComponent(str));
}

// ===== 老链路请求（保留兼容性） =====

/**
 * 完善请求数据（postData 用，jmallApiUrl 走通老接口）
 * @param {Object} data
 */
function completeData(data) {
	// 特殊字符编码
	data = JSON.parse(utils.htmlEncode(JSON.stringify(data)));
	// 补充会员名
	data.user_name = memberName;
	data.account_name = subMemberName;
	// 补充时间戳
	data.timestamp = timestamp();
	// 接口数据格式
	data.format = 'json';
	//处理表情
	for (let key in data) {
		data[key] = utf16toEntities(data[key] + '');
		data[key] = encodeURIComponent(data[key]);
	}
	// 补充签名
	data.sign = sign(data);
	return data;
}

/**
 * 请求接口数据
 * @param {Object} data 请求数据
 * @param {Object} callback 回调方法
 */
function postData(data, callback) {
	// 版本时间戳，用作前端强制刷新
	data.version_timestamp = versionTimestamp;

	// 完善请求数据
	data = completeData(data);

	// 请求接口
	axios.post(config.jmallApiUrl, data).then((response) => {
		if (response.status == 200) {
			// 特殊符号解码
			response.data = JSON.parse(JSON.stringify(response.data));
			callback && callback(response.data)
		}
	}).catch((error) => {
		console.info(error);
	});
}

/**
 * 请求接口数据（同步请求）
 * @param {Object} data 请求数据
 */
const postDataSync = (data) => {
	return new Promise((resolve, reject) => {
		// 完善请求数据
		data = completeData(data);
		// 请求接口
		axios.post(config.jmallApiUrl, data).then((response) => {
			if (response.status == 200) {
				// 特殊符号解码
				response.data = JSON.parse(utils.htmlDecode(JSON.stringify(response.data)));
				resolve(response.data)
			}
		}).catch((error) => {
			console.info(error);
		});
	})
}

/**
 * 请求吉客云接口数据（jkymini 改造后，内部走新链路，老链路通过 useJkymini=false 保留）
 * @param {Object} data 请求数据
 * @param {Object} callback 回调方法
 */
function jackyunPost(data, callback) {
	// 设置吉客云会员名
	data.target_member_name = memberName;
	data.sub_member_name = subMemberName;
	// 客户端名称
	data.client_name = "pc";
	// 用于验证版本的时间戳
	data.version_timestamp = versionTimestamp;
	// 用于验证版本的时间戳
	data.timestamp = timestamp();
	//生成内部验证串
	data.innerSign = getInnerSign(data);

	// 判断是否走 jkymini 链路
	if (shouldUseJkymini(data.method)) {
		// 走 jkymini：清掉 innerSign，避免 jkymini 网关验签错乱
		delete data.innerSign;
		// 走 jkymini 链路（同步处理 access_token 等参数）
		jackyunPostForCAuth(data.method, data, callback);
		return;
	}

	// 老链路：直接打到 admin 后台
	axios.post(config.jackyunApiUrl, data).then((response) => {
		if (response.status == 200) {
			callback && callback(response.data)
		}

		try {
			// 签名异常导致的请求，打印错误日志
			if (response.data.code != 200 && response.data.msg.includes("签名")) {
				console.error(`签名验证失败（${data.method}）:`, data, response.data);
			}
		} catch (e) {}
	}).catch((error) => {
		console.info(error);
	});
}

/**
 * 判断当前是否走 jkymini 网关
 *  优先级：
 *    1) 全局开关 config.pcUseJkymini（灰度用）
 *    2) method 前缀（jmall.* 走 jkymini，其他走 admin）
 */
function shouldUseJkymini(methodName) {
	// 全局开关优先
	if (config && config.pcUseJkymini === true) {
		// 但 jmall.login.* 不走 jkymini，走 jackyunUrl/auth/toc/login
		if (isToCLoginMethod(methodName)) {
			return false;
		}
		return isJkyminiMethod(methodName);
	}
	// 灰度期间，即使全局开关开了，jmall.login.* 也走老链路
	return false;
}

/**
 * 获取配置表参数
 */
function getParam(strParamkeys, callback) {
	postData({
		method: "common.cfg.getparam",
		param_key: strParamkeys
	}, (data) => {
		if (data.error_code == "0") {
			if (data.error_info) {
				let paramObj = JSON.parse(data.error_info.replace(/\\\n/g, '\\n').replace(/\\\r/g, '\\r'));
				callback && callback(paramObj);
			} else {
				callback && callback(null);
			}
		} else {
			callback && callback(null);
		}
	});
}

/**
 * 通用数据请求接口
 * @param {Object} url 请求地址
 * @param {Object} data 请求数据
 * @param {Object} callback 回调方法
 */
function commonPost(url, data, callback) {
	data.memberName = memberName;
	data.accountName = subMemberName;

	// 请求接口
	axios.post(url, data).then((res) => {
		callback && callback(res);
	}).catch((error) => {
		console.info(error);
	});
}

// ===== jkymini 链路（参照小程序 request-utils.js） =====

/**
 * 补充 jkymini 请求的通用参数
 *  - 根据 methodList 反查 url
 *  - 注入 memberName / subAccount / origin_name / toCAppKey / businessTypeCode
 *  - 注入 access_token
 *  - 用 jkyminiAuthInfo.appSecret 计算签名
 */
function __completeDataForCAuth(data, methodName) {
	let method = methodList[methodName];
	let url = method ? method.url : "";
	let isLoginMethod = isToCLoginMethod(methodName);

	// 获取系统配置中 jmall_api_version，没有则用默认 jmall
	let apiVersion = localStorage.getItem("pc_jmall_api_version") || jmallServiceName;

	// 注入公共参数
	data.origin_name = apiVersion;
	data.target_member_name = memberName || localStorage.getItem("JackyunUserName") || "";
	data.service_name = jkyminiAuthInfo.serviceName;
	data.appkey = jkyminiAuthInfo.appKey;

	// 注入 C 端登录相关参数
	data.memberName = data.target_member_name;
	data.subAccount = subMemberName || localStorage.getItem("SubMemberName") || "";
	data.toCAppKey = toCAppKey;
	data.businessTypeCode = toCBusinessTypeCode[apiVersion] || "TOC_LOGIN_JMALL";

	// 注入时间戳（毫秒级，与小程序保持一致）
	data.timestamp = timestampMs();

	// 注入 access_token（登录接口除外）
	if (!isLoginMethod) {
		let accessToken = localStorage.getItem("pc_accessToken_toCLogin") || "";
		// 同时写入 form 和 header，C 端网关两种都认
		data.access_token = `Bearer ${accessToken}`;
	}

	// 计算签名（用 jkymini 密钥）
	data.sign = __signForJkymini__(data, isLoginMethod);
}

/**
 * 把对象编码成 application/x-www-form-urlencoded 字符串
 *  - 用于 jkymini 网关请求（参照小程序 wx.request 默认行为）
 *  - axios.post 直接传对象会 JSON.stringify 成 JSON body，
 *    即便 header 写了 form-urlencoded 也无效。
 *  - 用 URLSearchParams 编码，签名里的 key=value 拼接顺序与 sign 一致
 */
function __toFormUrlEncoded(data) {
	const params = new URLSearchParams();
	Object.keys(data).forEach(key => {
		let value = data[key];
		if (value === undefined || value === null) return;
		params.append(key, String(value));
	});
	return params;
}

/**
 * 组装供原生 XMLHttpRequest 使用的 jkymini 请求配置。
 * 天爱验证码 SDK 自行发起请求，不能直接复用 jackyunPostForCAuth。
 */
function buildJkyminiRequest(methodName, data) {
	let method = methodList[methodName];
	if (!method) {
		return null;
	}

	const requestData = { ...(data || {}) };
	__completeDataForCAuth(requestData, methodName);

	const accessToken = localStorage.getItem("pc_accessToken_toCLogin") || "";
	const bearerToken = accessToken ? `Bearer ${accessToken}` : "";
	// 处理请求地址
	let targetUrl = method.url;
	// console.log('method.url',method.url)
	// console.log('requestData',requestData)
	if (requestData.origin_name != jmallServiceName) {
		targetUrl = method.url.replace(jmallServiceName, requestData.origin_name);
	}
	return {
		url: config.jkyminiUrl + targetUrl,
		data: __toFormUrlEncoded(requestData),
		headers: {
			"Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
			"mem_name": memberName || localStorage.getItem("JackyunUserName") || "",
			"sub_mem_name": subMemberName || localStorage.getItem("SubMemberName") || "",
			"access_token": bearerToken,
			"jmall_customer_id": localStorage.getItem("pc_customerId") || "18181818181818188",// 客户ID的key（带前缀，避免与框架内置key冲突），游客固定传18181818181818188
			"jmall_distributor_id": localStorage.getItem("pc_lastLoginDistributorId") || "",// 分销商ID的key（带前缀，避免与框架内置key冲突，游客不传此值
			"jmall_source_type": 'pc'//来源类型的key(pc还是小程序)  
		}
	};
}

/**
 * jkymini 网关的签名计算（参照小程序 __getSign__）
 *  - key 升序
 *  - 空值/null/undefined 跳过
 *  - sign 字段自己也跳过
 */
function __signForJkymini__(data, isLoginMethod) {
	let arrKey = Object.keys(data).sort().filter(t => t != 'sign');
	let preSign = [];
	arrKey.forEach(key => {
		let val = data[key];
		// 过滤空数据
		if (val === null || val === undefined || val === '') return;
		// 对象/数组转为字符串（jkymini 网关接受的是字符串形式）
		let strVal;
		if (typeof val === 'object') {
			strVal = JSON.stringify(val);
		} else {
			strVal = String(val);
		}
		data[key] = strVal;
		preSign.push(`${key}${strVal}`);
	});
	let secret = jkyminiAuthInfo.appSecret;
	return md5.md5(secret + preSign.join('') + secret).toLowerCase();
}

/**
 * jkymini 发送请求（带 C 端 token 验证）
 *  - 路径：methodList 里的 url，拼到 ${jkyminiUrl} 后
 *  - 请求头：mem_name, sub_mem_name, access_token
 *  - 数据：自动注入 C 端签名 + access_token
 */
function jackyunPostForCAuth(methodName, data, callback) {
	// 必须在游客态登录完成后（确保有 token），由 shouldUseJkymini() 控制
	let method = methodList[methodName];
	if (!method) {
		return callback && callback({
			code: 400,
			msg: "无效的method: " + methodName
		});
	}

	// 补充 C 端参数（含 access_token 和签名）
	__completeDataForCAuth(data, methodName);
	// 处理请求地址
	let targetUrl = method.url;
	if (data.origin_name != jmallServiceName) {
		targetUrl = method.url.replace(jmallServiceName, data.origin_name);
	}
	// 完整 url：jkymini 网关 + method url
	let url = config.jkyminiUrl + targetUrl;

	// 游客态 access_token：游客态登录后写入 localStorage.pc_accessToken_toCLogin。
	// C 端网关要求 header 里带 "Bearer xxx" 格式（与 form 字段 data.access_token 保持一致），
	// 否则请求会被网关侧 C 端 token 校验拦截。
	// 登录接口本身不需要 token（headers 里也不加）。
	let accessToken = localStorage.getItem("pc_accessToken_toCLogin") || "";
	let bearerToken = accessToken ? `Bearer ${accessToken}` : "";

	// 发送请求
	// 注意：jkymini 网关要求 form-urlencoded 提交（参照小程序 wx.request 的默认行为），
	//       而 axios 直接传对象会被 JSON.stringify 成 JSON body。
	//       所以必须先用 URLSearchParams 把 data 编码成 form 字符串。
	axios.post(url, __toFormUrlEncoded(data), {
		headers: {
			"Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
			"mem_name": memberName || localStorage.getItem("JackyunUserName") || "",
			"sub_mem_name": subMemberName || localStorage.getItem("SubMemberName") || "",
			"access_token": bearerToken,
			"jmall_customer_id": localStorage.getItem("pc_customerId") || "18181818181818188",// 客户ID的key（带前缀，避免与框架内置key冲突），游客固定传18181818181818188
			"jmall_distributor_id": localStorage.getItem("pc_lastLoginDistributorId") || "",// 分销商ID的key（带前缀，避免与框架内置key冲突，游客不传此值
			"jmall_source_type": 'pc'//来源类型的key(pc还是小程序)  
		}
	}).then((response) => {
		// jkymini 网关返回的结构：{ code, msg, result: { data } }
		let body = response.data || {};
		if (response.status === 200) {
			callback && callback(body);
		} else {
			callback && callback({
				code: response.status,
				msg: "请求失败",
				submsg: JSON.stringify(body)
			});
		}
	}).catch((error) => {
		console.error(`jkymini 请求失败（${methodName}）:`, error);
		callback && callback({
			code: 400,
			msg: "接口请求异常",
			submsg: String(error)
		});
	});
}

/**
 * 走 jackyunUrl（不是 jkymini 网关）的请求
 *  - 主要用于 C 端登录相关接口（jmall.login.*）
 *  - 走 ${jackyunAuthUrl}/auth/toc/login/* 等
 */
function jackyunPostForToCLogin(methodName, data, callback) {
	let method = methodList[methodName];
	if (!method) {
		return callback && callback({
			code: 400,
			msg: "无效的method: " + methodName
		});
	}

	// 补充 C 端参数
	__completeDataForCAuth(data, methodName);
	let targetUrl = method.url;
	if (data.origin_name != jmallServiceName) {
		targetUrl = method.url.replace(jmallServiceName, data.origin_name);
	}
	// 完整 url：jackyunUrl + method url
	let url = config.jackyunAuthUrl + targetUrl;

	// C 端登录接口是 GET（带 query 参数）
	let httpType = (method.type || "POST").toUpperCase();
	if (httpType === "GET") {
		axios.get(url, {
			params: data,
			headers: {
				"mem_name": memberName || localStorage.getItem("JackyunUserName") || "",
				"sub_mem_name": subMemberName || localStorage.getItem("SubMemberName") || ""
			}
		}).then((response) => {
			let body = response.data || {};
			if (response.status === 200) {
				callback && callback(body);
			} else {
				callback && callback({
					code: response.status,
					msg: "请求失败",
					submsg: JSON.stringify(body)
				});
			}
		}).catch((error) => {
			console.error(`C端登录请求失败（${methodName}）:`, error);
			callback && callback({
				code: 400,
				msg: "接口请求异常",
				submsg: String(error)
			});
		});
		return;
	}

	// POST 走表单方式
	axios.post(url, data, {
		headers: {
			"Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
			"mem_name": memberName || localStorage.getItem("JackyunUserName") || "",
			"sub_mem_name": subMemberName || localStorage.getItem("SubMemberName") || ""
		}
	}).then((response) => {
		let body = response.data || {};
		if (response.status === 200) {
			callback && callback(body);
		} else {
			callback && callback({
				code: response.status,
				msg: "请求失败",
				submsg: JSON.stringify(body)
			});
		}
	}).catch((error) => {
		console.error(`C端登录请求失败（${methodName}）:`, error);
		callback && callback({
			code: 400,
			msg: "接口请求异常",
			submsg: String(error)
		});
	});
}

// ===== 游客态 token 维护 =====

/**
 * 是否已经登录（PC 自己登录态判断，与 token 无关）
 */
function isLoggedIn() {
	return !!localStorage.getItem("pc_customerId");
}

/**
 * 保存游客态 access_token
 */
function __saveVisitorAccessToken(accessToken) {
	localStorage.setItem("pc_accessToken_toCLogin", accessToken);
	localStorage.setItem("pc_accessTokenToCLoginTime", String(Date.now()));
}

let visitorTokenTimer = null;

/**
 * 游客态假登录（启动时调用一次）
 *
 * 对应小程序 app.js 的 visitFakeLogin，用固定假账号登录拿到 access_token
 * PC 端做同样事情：
 *   1. 用 jmall.login.accountlogin 拿 access_token
 *   2. 存到 localStorage
 *   3. 启动 1.5h 后定时刷新（如果用户仍未登录）
 */
function visitFakeLogin() {
	return new Promise((resolve) => {
		let reqData = {
			loginName: VISITOR_FAKE_ACCOUNT,
			loginPwd: base64Encode(VISITOR_FAKE_ACCOUNT),
			loginNameByTel: '1',
			alreadyUrlEncode: false
		};

		jackyunPostForToCLogin('jmall.login.accountlogin', {
			jsonStr: reqData
		}, (res) => {
			try {
				// 响应结构：{ code, msg, result: { data: { token: { access_token } } } }
				let tokenObj = res && res.result && res.result.data && res.result.data.token;
				if (tokenObj && tokenObj.access_token) {
					__saveVisitorAccessToken(tokenObj.access_token);
					console.log('[游客态登录] 成功，已保存 access_token');
					// 启动定时刷新（仅在已登录用户不触发）
					__startVisitorTokenRefreshTimer();
				} else {
					console.log('[游客态登录] 失败：', res);
					// 失败时清空旧 token
					localStorage.removeItem("pc_accessToken_toCLogin");
				}
			} catch (e) {
				console.log('[游客态登录] 异常：', e);
			} finally {
				resolve();
			}
		});
	});
}

/**
 * 启动游客态 token 刷新定时器
 */
function __startVisitorTokenRefreshTimer() {
	// 清除已有定时器，避免重复
	if (visitorTokenTimer) {
		clearTimeout(visitorTokenTimer);
	}
	visitorTokenTimer = setTimeout(() => {
		__checkAndRefreshVisitorToken();
	}, VISITOR_TOKEN_REFRESH_MS);
}

/**
 * 检查并刷新游客态 token
 *  - 已登录用户不处理（用真用户 token）
 *  - 未登录用户重新执行 visitFakeLogin
 */
function __checkAndRefreshVisitorToken() {
	if (isLoggedIn()) {
		console.log('[游客态 token] 用户已登录，跳过刷新');
		return;
	}
	console.log('[游客态 token] 即将过期，执行刷新');
	visitFakeLogin();
}

// ===== 登录态 access_token 定时刷新（参考小程序 api-login.js） =====

// 登录态 token 刷新参数
//  - token 有效期 2 小时，提前 30 分钟刷新 → 阈值 1.5 小时
//  - 定时器间隔 1 小时（与小程序保持一致）
const USER_TOKEN_REFRESH_THRESHOLD_MS = 90 * 60 * 1000;   // 1.5 小时
const USER_TOKEN_REFRESH_INTERVAL_MS = 60 * 60 * 1000;   // 1 小时

// 登录态 token 刷新定时器（用 setTimeout 链式调用，避免 setInterval 在浏览器后台被节流）
let userTokenRefreshTimer = null;

/**
 * 基于 pc_lastLoginDistributorId 刷新 access_token
 *  - 对应小程序 refreshCurrCustomerInfoNew 里的 _loginFromOrigin({ distributorId }) 分支
 *  - 调用 jmall.login.accountlogin 接口，与游客态假登录走同一个 method，但入参只传 distributorId
 *  - 成功拿到新 token 后保存到 pc_accessToken_toCLogin，并重置 pc_accessTokenToCLoginTime
 *  - 失败时不抛错（避免影响主流程），下次定时器再重试
 */
function refreshAccessTokenByDistributorId() {
	return new Promise((resolve) => {
		const distributorId = localStorage.getItem("pc_lastLoginDistributorId");
		if (!distributorId) {
			console.log('[登录态 token 刷新] pc_lastLoginDistributorId 为空，跳过刷新');
			resolve(false);
			return;
		}

		jackyunPostForToCLogin('jmall.login.accountlogin', {
			jsonStr: {
				distributorId,
				alreadyUrlEncode: false
			}
		}, (res) => {
			try {
				let tokenObj = res && res.result && res.result.data && res.result.data.token;
				if (tokenObj && tokenObj.access_token) {
					__saveVisitorAccessToken(tokenObj.access_token);
					console.log('[登录态 token 刷新] 成功，已更新 access_token');
					resolve(true);
				} else {
					console.log('[登录态 token 刷新] 失败：', res);
					resolve(false);
				}
			} catch (e) {
				console.log('[登录态 token 刷新] 异常：', e);
				resolve(false);
			}
		});
	});
}

/**
 * 停止登录态 token 刷新定时器
 */
function __stopUserTokenRefreshTimer() {
	if (userTokenRefreshTimer) {
		clearTimeout(userTokenRefreshTimer);
		userTokenRefreshTimer = null;
	}
}

/**
 * 启动登录态 token 刷新定时器（1 小时后触发）
 *  - 触发时如果用户已登录且 token 接近过期则刷新
 *  - 链式 setTimeout 而非 setInterval，避免浏览器后台节流导致刷新延后
 */
function __startUserTokenRefreshTimer() {
	// 清除已有定时器，避免重复
	__stopUserTokenRefreshTimer();

	userTokenRefreshTimer = setTimeout(() => {
		__checkAndRefreshUserToken();
	}, USER_TOKEN_REFRESH_INTERVAL_MS);
}

/**
 * 检查并刷新登录态 token
 *  - 仅在已登录用户（有 pc_customerId + pc_lastLoginDistributorId）时执行
 *  - token 接近过期（>= 1.5 小时）则主动刷新
 *  - 否则仅重启定时器（按 1 小时间隔持续检查）
 */
function __checkAndRefreshUserToken() {
	// 用户已退出登录，停止刷新
	if (!isLoggedIn()) {
		console.log('[登录态 token 刷新] 用户未登录，停止刷新');
		__stopUserTokenRefreshTimer();
		return;
	}

	// 读取 token 获取时间，没有则跳过（让定时器持续运行，下次再判断）
	const tokenTime = parseInt(localStorage.getItem("pc_accessTokenToCLoginTime") || "0", 10);
	if (tokenTime > 0) {
		const elapsed = Date.now() - tokenTime;
		if (elapsed >= USER_TOKEN_REFRESH_THRESHOLD_MS) {
			console.log(`[登录态 token 刷新] token 已过 ${Math.round(elapsed / 1000 / 60)} 分钟，执行刷新`);
			refreshAccessTokenByDistributorId().finally(() => {
				// 刷新后继续启动下一轮定时器
				__startUserTokenRefreshTimer();
			});
			return;
		}
	}

	// token 还在有效期内（或没有时间记录），按 1 小时间隔继续检查
	__startUserTokenRefreshTimer();
}

/**
 * 可见性变化处理（解决浏览器后台 setTimeout 被节流导致 token 过期的场景）
 *  - 对应小程序 onShow 中已登录用户的 token 检查逻辑
 *  - 仅在切回前台时主动检查一次
 */
function __onVisibilityChangeForToken() {
	if (document.visibilityState !== 'visible') {
		return;
	}
	if (!isLoggedIn()) {
		return;
	}
	// 直接检查一次，不重启定时器（定时器仍在按 1 小时间隔运行）
	const tokenTime = parseInt(localStorage.getItem("pc_accessTokenToCLoginTime") || "0", 10);
	if (!tokenTime) {
		return;
	}
	const elapsed = Date.now() - tokenTime;
	if (elapsed >= USER_TOKEN_REFRESH_THRESHOLD_MS) {
		console.log(`[登录态 token 刷新] 切回前台，token 已过 ${Math.round(elapsed / 1000 / 60)} 分钟，执行刷新`);
		refreshAccessTokenByDistributorId();
	}
}

// 注册全局 visibilitychange 监听（模块加载时执行一次即可）
if (typeof document !== 'undefined' && document.addEventListener) {
	document.addEventListener('visibilitychange', __onVisibilityChangeForToken);
}

// ===== 导出 =====

export default {
	// 老接口（保留兼容）
	postData,
	postDataSync,
	jackyunPost,
	getParam,
	commonPost,
	getUserName,

	// jkymini 新增接口
	jackyunPostForCAuth,         // jkymini 网关请求（带 C 端 token 验证）
	buildJkyminiRequest,         // 供第三方 SDK 使用的 jkymini 请求配置
	jackyunPostForToCLogin,      // C 端登录相关（不带 token，走 jackyunUrl）
	visitFakeLogin,              // 游客态假登录
	sign,                        // 暴露签名方法（带密钥参数）
	methodList,                  // 暴露 method 清单（便于调试）
	isJkyminiMethod,             // 判断是否走 jkymini
	isToCLoginMethod,            // 判断是否走 C 端登录
	shouldUseJkymini,            // 综合判断是否走 jkymini
	pcUseJkymini: config.pcUseJkymini,  // 全局开关值（供 main.js 启动时决策）
	getJmallApiVersion,          // 获取商城版本（jkymini 前置，必须在 visitFakeLogin 之前调用）

	// 登录态 token 刷新（对应小程序 refreshCurrCustomerInfoNew 维持登录态的能力）
	refreshAccessTokenByDistributorId,  // 用 pc_lastLoginDistributorId 调一次 accountlogin 拿新 token
	startUserTokenRefreshTimer: __startUserTokenRefreshTimer,  // 启动 1 小时定时刷新
	stopUserTokenRefreshTimer: __stopUserTokenRefreshTimer     // 停止定时刷新（退出登录时调用）
}
